import React, { useState } from "react";

import Typography from "@mui/material/Typography";

import SettingAccordion from "@app/analytics/account/components/SettingAccordion";
import MinimumNoticeInput, {
  MINIMUM_NOTICE_BUFFER,
} from "@app/analytics/components/MinimumNoticeInput";
import SettingsToggle from "@app/analytics/components/SettingsToggle";
import { toast } from "@app/common/hooks/useToasts";
import type { UserRssLinkSettings } from "@app/common/types";

import Stack from "@rui/Stack";

const MinimumSchedulingNotice = ({
  onSettingUpdate,
  settings,
}: {
  onSettingUpdate: (
    update: Partial<UserRssLinkSettings>,
    opts?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  settings: UserRssLinkSettings;
}) => {
  const [pendingSchedulingBuffer, setPendingSchedulingBuffer] = useState<
    number | null
  >(null);

  const handleToggle = (event: React.ChangeEvent, checked: boolean) => {
    onSettingUpdate(
      {
        minimumSchedulingBuffer: checked
          ? MINIMUM_NOTICE_BUFFER.DEFAULT
          : MINIMUM_NOTICE_BUFFER.MIN,
      },
      {
        onError: () => {
          toast.error(
            "Something went wrong updating your settings. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  const handleBlur = () => {
    if (
      pendingSchedulingBuffer == null ||
      pendingSchedulingBuffer === settings.minimumSchedulingBuffer ||
      pendingSchedulingBuffer > MINIMUM_NOTICE_BUFFER.MAX
    ) {
      return;
    }

    onSettingUpdate(
      { minimumSchedulingBuffer: pendingSchedulingBuffer },
      {
        onSuccess: () => setPendingSchedulingBuffer(null),
        onError: () => {
          setPendingSchedulingBuffer(null);
          toast.error(
            "Something went wrong updating your settings. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  return (
    <Stack sx={{ px: 2 }} spacing={1.5}>
      <SettingAccordion
        title="Minimum notice"
        description="Enforce minimum notice for scheduling meetings"
        defaultExpanded={settings.minimumSchedulingBuffer > 0}
        expandDisabled={!settings.minimumSchedulingBuffer}
        Toggle={
          <SettingsToggle
            checked={settings.minimumSchedulingBuffer > 0}
            onChange={handleToggle}
          />
        }
        sx={{ py: 0 }}
        detailsSx={{ px: 0 }}
      >
        <Stack spacing={1}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
            Don't allow others to schedule within:
          </Typography>
          <MinimumNoticeInput
            onBlur={handleBlur}
            onChange={setPendingSchedulingBuffer}
            value={pendingSchedulingBuffer ?? settings.minimumSchedulingBuffer}
          />
        </Stack>
      </SettingAccordion>
    </Stack>
  );
};

export default MinimumSchedulingNotice;
